import { useRef, useState, useEffect, ReactNode } from "react";
import { CSSTransition } from "react-transition-group";
import useOutsideClick from "@hooks/useOutsideClick";
import { Portal } from '@components/Portal';
import cn from "classnames";
import styles from "./DropdownSimple.module.scss";

interface DropdownSimpleProps {
  className?: string;
  triggerContent?: ReactNode;
  dropdownContent?: ReactNode;
  menuAlign?: "top" | "bottom" | "left" | "right";
  menuPosition?: "top" | "bottom" | "left" | "right";
  menuWidth?: string;
  disabled?: boolean;
  fixed?: boolean;
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}

function DropdownSimple({
  // className,
  triggerContent,
  dropdownContent,
  menuAlign,
  menuPosition,
  menuWidth,
  disabled,
  fixed,
  isOpen,
  setIsOpen,
}: DropdownSimpleProps) {
  // const [isOpen, setIsOpen] = useState(false);
  const [positionObj, setPositionObj] = useState({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const position = menuPosition || "bottom";
  const align = menuAlign ?? ((position === "bottom" || position === "top") ? "right" : "top");

  const getPosition = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current && triggerRef.current.getBoundingClientRect();
    const menuRect = dropdownMenuRef.current && dropdownMenuRef.current.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? 0;
    const bottom = document.body.clientHeight - rect.bottom;

    const W = document.documentElement.clientWidth;
    const H = document.documentElement.clientHeight;

    if (position === "right") {
      return {
        ...((align === "top" || bottom < menuHeight) ? {
          bottom: `calc(${H}px - ${rect.bottom}px - ${window.scrollY}px)`,
        } : {
          top: `calc(${rect.top}px + ${window.scrollY}px)`,
        }),
        left: `calc(${styles.margin} + ${rect.right}px)`,
      };
    }
    if (position === "left") {
      return {
        ...((align === "top" || bottom < menuHeight) ? {
          bottom: `calc(${H}px - ${rect.bottom}px - ${window.scrollY}px)`,
        } : {
          top: `calc(${rect.top}px + ${window.scrollY}px)`,
        }),
        right: `calc(${W}px - ${window.scrollX}px - ${rect.left}px + ${styles.margin})`
      };
    }
    if (position === "bottom") {
      return {
        top: `calc(${rect.bottom}px + ${window.scrollY}px + ${styles.margin})`,
        ...((align === "left") ? {
          left: `calc(${window.scrollX}px + ${rect.left}px)`
        } : {
          right: `calc(${W}px - ${window.scrollX}px - ${rect.right}px)`
        }),
      };
    }
    if (position === "top") {
      return {
        bottom: `calc(${H}px - ${rect.top}px - ${window.scrollY}px + ${styles.margin})`,
        ...((align === "left") ? {
          left: `calc(${window.scrollX}px + ${rect.left}px)`
        } : {
          right: `calc(${W}px - ${window.scrollX}px - ${rect.right}px)`
        }),
      };
    }
    return {}
  }

  const handlePositionChange = () => {
    setPositionObj(getPosition());
  }

  useEffect(() => {
    document.addEventListener('scroll', handlePositionChange, true);
  }, [])

  useEffect(() => {
    if (triggerRef.current) {
      handlePositionChange();
    }
  }, [dropdownMenuRef.current, triggerRef.current, isOpen])

  useOutsideClick<HTMLDivElement>(dropdownMenuRef, () => setIsOpen(false));

  const fromModal = triggerRef?.current && triggerRef.current.closest(".modal");

  return (
    <>
      <div
        ref={triggerRef}
        className={cn(styles.selected, disabled && styles.disabled)}
        onClick={() => (!disabled && setIsOpen(!isOpen))}
      >
        {triggerContent}
      </div>
      <Portal>
        <CSSTransition
          in={isOpen}
          timeout={300}
          classNames={{
            enter: styles.menuEnter,
            enterActive: styles.menuEnterActive,
            exit: styles.menuExit,
            exitActive: styles.menuExitActive,
          }}
          mountOnEnter
          unmountOnExit
          nodeRef={dropdownMenuRef}
        >
          <div
            className={cn(
              styles.menu,
              styles[`position-${position}`],
              fromModal && styles.fromModal,
            )}
            // Меню рендерится в портале вне DOM модалки — атрибут позволяет вложенным
            // тултипам понять, что они открыты из модалки, и подняться над ней (z-index)
            data-from-modal={fromModal ? "" : undefined}
            style={{
              ...positionObj,
              maxWidth: menuWidth,
              position: fixed ? "fixed" : "absolute",
            }}
            ref={dropdownMenuRef}
          >
            {dropdownContent}
          </div>
        </CSSTransition>
      </Portal>
    </>
  );
};

export default DropdownSimple;
